import { motion } from "framer-motion";
import { MapPin, Phone, Instagram, Facebook, MessageCircle, Clock } from "lucide-react";
import { WHATSAPP_URL } from "./Navbar";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const INFO = [
  {
    icon: MapPin,
    label: "Dove siamo",
    value: "Via S. Vigilio, 8 — 38122 Trento (TN)",
    href: "https://www.google.com/maps/search/?api=1&query=Via+S.+Vigilio+8+Trento",
    testid: "contact-address",
  },
  {
    icon: Phone,
    label: "Telefono",
    value: "0461 230096",
    href: "tel:+390461230096",
    testid: "contact-phone",
  },
];

export const Contatti = () => (
  <section id="contatti" className="py-24 sm:py-32" data-testid="contatti-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-xs uppercase tracking-[0.3em] font-semibold text-[#84232a] mb-4"
            data-testid="contatti-eyebrow"
          >
            Contatti
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="font-display text-4xl sm:text-5xl font-semibold text-black leading-tight"
            data-testid="contatti-title"
          >
            Vieni a trovarci,{" "}
            <span className="font-script text-[#84232a] font-normal">ti aspettiamo.</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-[#4a423d] text-base sm:text-lg leading-relaxed mt-6 max-w-md"
          >
            Siamo nel centro storico di Trento, a due passi da Piazza Duomo. Passa per
            una colazione, un aperitivo o per ordinare la tua torta speciale.
          </motion.p>

          <div className="mt-10 space-y-5">
            {INFO.map((item, i) => (
              <motion.a
                key={item.label}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={item.testid}
                className="group flex items-center gap-5"
              >
                <span className="w-12 h-12 shrink-0 rounded-full bg-[#84232a]/10 text-[#84232a] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#84232a] group-hover:text-[#fbf6f1]">
                  <item.icon className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-[#4a423d]">
                    {item.label}
                  </span>
                  <span className="block font-medium text-black mt-0.5 group-hover:text-[#84232a] transition-colors duration-300">
                    {item.value}
                  </span>
                </span>
              </motion.a>
            ))}
            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex items-center gap-5"
              data-testid="contact-hours"
            >
              <span className="w-12 h-12 shrink-0 rounded-full bg-[#84232a]/10 text-[#84232a] flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-[#4a423d]">
                  Orari
                </span>
                <span className="block font-medium text-black mt-0.5">
                  Martedì – Domenica: 8:00 – 19:30 (orario continuato)
                </span>
                <span className="block text-sm text-[#4a423d]">Lunedì: chiuso</span>
              </span>
            </motion.div>
          </div>

          <motion.div
            variants={fadeUp}
            custom={4}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contatti-whatsapp-cta"
              className="group flex items-center gap-3 bg-[#84232a] text-[#fbf6f1] px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[#63191f] transition-colors duration-300"
            >
              <MessageCircle className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
              Scrivici su WhatsApp
            </a>
            <a
              href="tel:+390461230096"
              data-testid="contatti-call-cta"
              className="group flex items-center gap-3 border border-black/20 px-7 py-3.5 rounded-full text-sm font-medium text-black hover:border-[#84232a] hover:text-[#84232a] transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              Chiama ora
            </a>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/pasticceriasanvigilio/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram-link"
                aria-label="Instagram di Pasticceria San Vigilio"
                className="w-11 h-11 rounded-full border border-black/15 flex items-center justify-center text-black hover:border-[#84232a] hover:text-[#84232a] transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/pasticceriasanvigilio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook-link"
                aria-label="Facebook di Pasticceria San Vigilio"
                className="w-11 h-11 rounded-full border border-black/15 flex items-center justify-center text-black hover:border-[#84232a] hover:text-[#84232a] transition-colors duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden border border-[#e6dacb] min-h-[380px] lg:min-h-full shadow-lg shadow-black/5"
          data-testid="contact-map"
        >
          <iframe
            title="Mappa: Pasticceria San Vigilio, Via S. Vigilio 8, Trento"
            src="https://www.google.com/maps?q=Via%20S.%20Vigilio%208%2C%2038122%20Trento%20TN&output=embed"
            className="absolute inset-0 w-full h-full"
            style={{ border: 0, filter: "sepia(0.15) saturate(0.85)" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </div>
  </section>
);
